import { PlusIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import React, { useEffect, useState, useCallback } from "react";
import PageTransition from "../../components/common/PageTransition";
import HeaderSection from "../../components/HeaderSection";
import UserManager from "../../feature/user/UserManager";
import { useAppSelector, useAppDispatch } from "../../store";
import {
  selectUsers,
  selectUserLoading,
  selectUserError,
  selectActiveUsers,
  selectInactiveUsers,
  selectUserPagination,
  fetchUsers,
  selectedUsersCount
} from "../user/userSlice";
import { selectBranches, fetchBranches } from "../branch/branchSlice";
import SearchBar from "../../components/searchbar";
import { Button } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { User, UserFormData } from "../user/user";
import EditUserModal from "./EditUserModal";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import PageWrapper from "../../components/common/PageWrapper";
import CloseConfirmationDialogue from "../../components/common/__tests__/CloseConfirmationDialogue";
import { useDebounce } from "../../hooks/useDebounce";

const Users: React.FC = () => {
  const dispatch = useAppDispatch();
  const users = useAppSelector(selectUsers);
  const loading = useAppSelector(selectUserLoading);
  const error = useAppSelector(selectUserError);
  const branches = useAppSelector(selectBranches);
  const activeUsersCount = useAppSelector(selectActiveUsers);
  const inactiveUsersCount = useAppSelector(selectInactiveUsers);
  const pagination = useAppSelector(selectUserPagination);
  const counts = useAppSelector(selectedUsersCount);

  // Local state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<"all" | "active" | "inactive">("all");
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserFormData | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [modalUser, setModalUser] = useState<User | null>(null);
  const [closeDialog, setCloseDialog] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFormFilled, setIsFormFilled] = useState(false);

  // Debounce search query to prevent excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  // Track if search is in progress
  const isSearching = searchQuery !== debouncedSearchQuery;

  const [searchParams] = useSearchParams();

   const handleFormDirty = (isFilled: boolean) => {
      setIsFormFilled(isFilled);
    };

  // Initial data fetch - runs only once on mount
  useEffect(() => {
    dispatch(fetchBranches());
  }, [dispatch]);

  useEffect(() => {
    console.log("Users counts",counts);
  },[counts])

  // Handle URL search params
  useEffect(() => {
    const branchId = searchParams.get("branchId");
    const filter = searchParams.get("filterType");

    if (branchId) setSelectedBranch(branchId);
    if (filter === "all" || filter === "active" || filter === "inactive") {
      setFilterType(filter as "all" | "active" | "inactive");
    }
  }, [searchParams]);

  // Handle search and filter changes with debouncing
  useEffect(() => {
    dispatch(fetchUsers({
      page: DEFAULT_PAGINATION.page,
      limit: DEFAULT_PAGINATION.limit,
      search: debouncedSearchQuery,
      status: filterType === "all" ? "" : filterType,
      branchId: selectedBranch || undefined,
    })).finally(() => {
      setIsInitialLoad(false);
    });
  }, [debouncedSearchQuery, filterType, selectedBranch, dispatch]);

  // Memoized callback for opening create modal
  const handleOpenCreateModal = useCallback(() => {
    setModalMode("create");
    setModalUser(null);
    setShowUserModal(true);
  }, []);

  // Memoized callback for clearing filters
  const handleClearFilters = useCallback(() => {
    setSearchQuery("");
    setSelectedBranch(null);
  }, []);

  // Memoized callback for retrying fetch
  const handleRetry = useCallback(() => {
    dispatch(fetchUsers({
      page: pagination.page,
      limit: pagination.limit,
      search: debouncedSearchQuery,
      status: filterType === "all" ? "" : filterType,
      branchId: selectedBranch || undefined,
    }));
  }, [dispatch, pagination.page, pagination.limit, debouncedSearchQuery, filterType, selectedBranch]);

  // Memoized callback for modal success
  const handleModalSuccess = useCallback(() => {
    setShowUserModal(false);
    setModalUser(null);
    setIsFormFilled(false);
    dispatch(fetchUsers({
      page: pagination.page,
      limit: pagination.limit,
      search: debouncedSearchQuery,
      status: filterType === "all" ? "" : filterType,
      branchId: selectedBranch || undefined,
    }));
  }, [dispatch, pagination.page, pagination.limit, debouncedSearchQuery, filterType, selectedBranch]);

  // Memoized callback for closing modal
  const handleCloseModal = useCallback(() => {
    // setCloseDialog(true);
    if (isFormFilled) {
      setCloseDialog(true);
    } else {
      setShowUserModal(false);
      setModalUser(null);      
      dispatch(fetchUsers({
        page: pagination.page,
        limit: pagination.limit,
        search: searchQuery,
        status: filterType === "all" ? "" : filterType,
        branchId: selectedBranch || undefined,
      }));
      
    }
  }, []);

  // Memoized callback for confirming close
  const handleConfirmClose = useCallback(() => {
    // setIsFormFilled(false);
    setCloseDialog(false);
    setShowUserModal(false);
    setSelectedUser(null);
  }, []);

  // Loading state - only show full page loader on initial load
  if (loading && users.length === 0 && isInitialLoad) {
    return (
      <PageTransition>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </PageTransition>
    );
  }

  // Error state - only show full page error on initial load
  if (error && users.length === 0 && isInitialLoad) {
    return (
      <PageTransition>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="mb-4">
                <UserGroupIcon className="h-12 w-12 text-red-400 mx-auto" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                Error Loading Users
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageWrapper>
      <HeaderSection
        title="Users"
        description="Manage your team members and their access levels"
        icon={
          <UserGroupIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
        }
        buttonIcon={<PlusIcon className="h-5 w-5 mr-2" />}
        buttonText="New User"
        onButtonClick={handleOpenCreateModal}
      />

      <div className="w-full flex-1 pb-5">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="flex-1">
            <div className="relative">
              <SearchBar
                placeholder="Search users"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                </div>
              )}
            </div>
          </div>

          <div className="flex mb-4">
            {(searchQuery || selectedBranch) && (
              <Button
                onClick={handleClearFilters}
                className="flex items-center justify-center px-4 py-3 text-base font-medium text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/10 transition-colors"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
      >
        <div
          className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6 cursor-pointer"
          onClick={() => setFilterType("all")}
        >
          <div className="text-primary-400">Total Users</div>
          <div className="text-3xl font-bold text-secondary-900 dark:text-white mt-2">
            {counts.total}
          </div>
        </div>
        <div
          className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6 cursor-pointer"
          onClick={() => setFilterType("active")}
        >
          <div className="text-green-600">Active Users</div>
          <div className="text-3xl font-bold text-secondary-900 dark:text-white mt-2">
            {counts?.active}
          </div>
        </div>
        <div
          className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6 cursor-pointer"
          onClick={() => setFilterType("inactive")}
        >
          <div className="text-red-600">Inactive Users</div>
          <div className="text-3xl font-bold text-secondary-900 dark:text-white mt-2">
            {counts?.inactive}
          </div>
        </div>
      </motion.div>

      {/* User Manager Component */}
      <UserManager
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        searchQuery={debouncedSearchQuery}
        filterType={filterType}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
      />

      <EditUserModal
        isOpen={showUserModal}
        // onClose={handleCloseModal}
        onRequestClose={(hasData) => {
          console.log("-----------------------hasdata:---------------------------",hasData)
          if (hasData) {
            setCloseDialog(true)
          } else {
            console.log("++++++++++++++++++++++++++hasdata:+++++++++++++++++++++",hasData)
            setShowUserModal(false);
            setModalUser(null);
          }
        }}
        onSuccess={handleModalSuccess}
        mode={modalMode}
        user={modalUser}
        // onFormDirty={(dirty: boolean) => handleFormDirty(dirty)}
      />

      {closeDialog && (
        <CloseConfirmationDialogue
          open={closeDialog}
          onClose={() => setCloseDialog(false)}
          onConfirm={handleConfirmClose}
          title=""
          message="Are you sure you want to close this user model?"
        />
      )}
    </PageWrapper>
  );
};

export default Users;
